import { useQuery } from '@tanstack/react-query';
import { getQuizSparkLoopSettings, isSparkLoopPilotPartner } from './internal/settings';
export { isSparkLoopPilotPartner } from './internal/settings';
export type { QuizSparkLoopSettings } from './internal/settings';
import React, { lazy, useMemo } from 'react';

const QuizNewsletterInvitation = lazy(() => import('./QuizNewsletterInvitation'));

/** Route-owned test-partner opt-in using resolved Quiz ownership. */
export function getQuizNewsletterInvitationPreview(
  search: string,
  partnerId: string | null | undefined
) {
  const testPartnerId = String(import.meta.env.VITE_SPARKLOOP_TEST_PARTNER_ID ?? '').trim();
  const enabled =
    typeof window !== 'undefined' &&
    import.meta.env.DEV &&
    Boolean(testPartnerId) &&
    partnerId === testPartnerId &&
    new URLSearchParams(search).get('sparkloop_preview') === 'true' &&
    Boolean(import.meta.env.VITE_SPARKLOOP_PUBLICATION_ID) &&
    ['localhost', '127.0.0.1', '[::1]'].includes(window.location.hostname);
  return enabled ? QuizNewsletterInvitation : undefined;
}

/** The saved Quiz owner and server approval govern production access. */
export function useQuizNewsletterInvitation(
  partnerId: string | null | undefined,
  quizId: string | null | undefined,
  enabled: boolean
) {
  const settings = useQuery({
    queryKey: ['quiz-sparkloop-settings', quizId],
    queryFn: () => getQuizSparkLoopSettings(quizId!),
    enabled: enabled && isSparkLoopPilotPartner(partnerId) && Boolean(quizId),
    retry: false,
    staleTime: 30_000,
  });
  const config =
    enabled &&
    !settings.isError &&
    isSparkLoopPilotPartner(partnerId) &&
    settings.data?.partnerId === partnerId &&
    settings.data.quizId === quizId
      ? settings.data
      : null;
  const Invitation = useMemo(
    () =>
      config
        ? function ConfiguredInvitation(props: {
            context: { partnerId: string; quizId: string; editionId: string };
            onContinue: () => void;
          }) {
            return React.createElement(QuizNewsletterInvitation, { ...props, settings: config });
          }
        : undefined,
    [config]
  );
  return { Invitation, placement: config?.placement };
}
